import { useId } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Button from './Button.jsx';
import Tooltip from './Tooltip.jsx';

/**
 * Selection bar shown above a list/table while rows are selected: count label, bulk actions,
 * an optional "Select all N" control (when the selection can span more than one page) and Clear.
 * Fully prop-driven: the consumer passes translated strings (`selectedLabel`, action labels,
 * `clearLabel`, `selectAllLabel`); the bar never hardcodes "members" or "rows".
 *
 * A disabled action keeps its `disabledReason` as a tooltip and as an accessible description.
 *
 * @param {object} props
 * @param {string} props.selectedLabel — Exact count text, e.g. `"3 members selected"`.
 * @param {{ id: string, label: string, onClick: () => void, disabled?: boolean, disabledReason?: string, icon?: import('lucide-react').LucideIcon, variant?: 'primary'|'classicPrimary'|'secondary'|'tertiary'|'ghost'|'danger' }[]} [props.actions]
 * @param {() => void} props.onClear
 * @param {string} [props.clearLabel='Clear selection'] — `aria-label` of the Clear control.
 * @param {number} [props.selectedCount] — Needed with `totalCount` to hide "Select all" once everything is selected.
 * @param {number} [props.pageCount] — Rows on the current page.
 * @param {number} [props.totalCount] — Rows across all pages; "Select all" shows when `totalCount > pageCount`.
 * @param {() => void} [props.onSelectAll]
 * @param {string} [props.selectAllLabel] — Translated label; required with `onSelectAll` (falls back to English `Select all {totalCount}`).
 * @param {'end'|'start'} [props.actionsAlign='end'] — `end` (default) pushes the actions and Clear to the right;
 *   `start` puts the actions right after the count and keeps only Clear on the right.
 * @param {string} [props.className]
 */
const BulkActionBar = ({
  selectedLabel,
  actions = [],
  onClear,
  clearLabel = 'Clear selection',
  selectedCount,
  pageCount,
  totalCount,
  onSelectAll,
  selectAllLabel,
  actionsAlign = 'end',
  className = '',
}) => {
  const barId = useId();
  const showSelectAll =
    typeof onSelectAll === 'function' &&
    typeof totalCount === 'number' &&
    typeof pageCount === 'number' &&
    totalCount > pageCount &&
    !(typeof selectedCount === 'number' && selectedCount >= totalCount);

  const clearButton = <Button variant="ghost" size="sm" leftIcon={X} aria-label={clearLabel} onClick={onClear} />;

  return (
    <div
      className={[
        'flex flex-wrap items-center gap-3 rounded-lg border border-[#2173FF]/40 bg-[#2173FF]/10 px-4 py-2.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span role="status" className="text-sm font-medium text-content">
        {selectedLabel}
      </span>

      {showSelectAll ? (
        <Button variant="ghost" size="sm" onClick={onSelectAll}>
          {selectAllLabel ?? `Select all ${totalCount}`}
        </Button>
      ) : null}

      <div className={actionsAlign === 'start' ? 'flex flex-wrap items-center gap-2' : 'ml-auto flex flex-wrap items-center gap-2'}>
        {actions.map((action) => {
          const reasonId = `${barId}-reason-${action.id}`;
          const hasReason = Boolean(action.disabled && action.disabledReason);
          // A disabled <button> swallows mouse events and cannot take focus, so with a reason the
          // button ignores the pointer and a focusable wrapper owns hover/focus for the Tooltip.
          const button = (
            <Button
              variant={action.variant ?? 'secondary'}
              size="sm"
              leftIcon={action.icon}
              disabled={action.disabled}
              aria-describedby={hasReason ? reasonId : undefined}
              className={hasReason ? 'pointer-events-none' : undefined}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          );
          return hasReason ? (
            <Tooltip key={action.id} content={action.disabledReason} position="bottom" size="compact">
              <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">
                {button}
              </span>
              <span id={reasonId} className="sr-only">
                {action.disabledReason}
              </span>
            </Tooltip>
          ) : (
            <span key={action.id}>{button}</span>
          );
        })}
        {actionsAlign === 'start' ? null : clearButton}
      </div>
      {actionsAlign === 'start' ? <div className="ml-auto">{clearButton}</div> : null}
    </div>
  );
};

BulkActionBar.propTypes = {
  selectedLabel: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      disabledReason: PropTypes.string,
      icon: PropTypes.elementType,
      variant: PropTypes.oneOf(['primary', 'classicPrimary', 'secondary', 'tertiary', 'ghost', 'danger']),
    }),
  ),
  onClear: PropTypes.func.isRequired,
  clearLabel: PropTypes.string,
  selectedCount: PropTypes.number,
  pageCount: PropTypes.number,
  totalCount: PropTypes.number,
  onSelectAll: PropTypes.func,
  // Required whenever `onSelectAll` is passed, so a screen cannot ship the English default.
  selectAllLabel: (props, propName, componentName) => {
    const value = props[propName];
    if (value != null && typeof value !== 'string') {
      return new Error(`Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`string\`.`);
    }
    if (typeof props.onSelectAll === 'function' && !value) {
      return new Error(`\`${propName}\` is required in \`${componentName}\` when \`onSelectAll\` is provided (pass a translated label).`);
    }
    return null;
  },
  actionsAlign: PropTypes.oneOf(['end', 'start']),
  className: PropTypes.string,
};

export default BulkActionBar;
